import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import indexOf from 'lodash/indexOf';
import {
  applySnapshot,
  cast,
  flow,
  getRoot,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import moment from 'moment';
import {
  collectConversationIds,
  downloadBlobFile,
  formatDateToISO,
  killNulls,
  normalizeActionDates,
  openBlobInNewTabAndPrint,
  prepareDatesInData,
  RequestListItem,
  TenantHouseProviderPair,
} from 'utils';
import {
  IAttachmentFile,
  IRelatedRequest,
  IRequest,
  IRequestTemplate,
  ISignal,
  SendingChannel,
  TicketType,
} from 'types';
import {
  RequestHistoryType,
  SignalHistoryType,
} from '../../views/authorized/pages/requests/tabs/requests/request-tab/changes-history/types';
import { Catalogue, Crystal, Files, Messenger, Requests } from 'http-requests';
import { ICompletionAct } from 'http-requests/catalogue/types';
import { IParams } from 'http-requests/types';
import {
  IRequestKinds,
  requestFileFieldType,
} from 'views/authorized/pages/requests/types';
import type { ISelectValue } from 'components/ui/select';
import { message } from 'components/ui/notifications/message';

import {
  CurrentSelectedCoordinators,
  RequestStateModel,
} from './general-models';

/* Сообщение и обработка конфликта оптимистичной блокировки (HTTP 409):
 * карточку изменили в другой вкладке/сессии. Бэкенд отклонил запись, чтобы
 * не затереть чужую правку - показываем пользователю предупреждение и
 * перечитываем заявку, чтобы он увидел актуальные данные и свежую версию. */
const CONCURRENT_UPDATE_MESSAGE =
  'Заявка была изменена в другой вкладке или другим сотрудником. ' +
  'Данные обновлены - проверьте их и при необходимости повторите изменение.';

const handleConcurrentUpdate = async (self: any, error: any) => {
  if (error?.status !== 409) {
    return;
  }

  message({ type: 'warning', text: CONCURRENT_UPDATE_MESSAGE });

  const id = self.current?._id || self.currentOnEdit?._id;
  if (id) {
    await self.getRequestDetail(
      id,
      undefined,
      self.current?.item_type === 'signal'
    );
  }
};

export const RequestStatusMST = types.enumeration('RequestStatus', [
  'accepted',
  'performed',
  'run',
  'delayed',
  'abandonment',
  'refusal',
  'hidden',
]);

export interface IRequestCategory extends Instance<typeof CategoryModel> {}
export interface IRequestSubcategory extends Instance<
  typeof SubcategoryModel
> {}
export interface IWorkArea extends Instance<typeof WorkAreaModel> {}

const MLTranscriptPhraseModel = types.model({
  speaker: types.optional(types.string, ''),
  message: types.optional(types.maybeNull(types.string), null),
});

const MLTranscriptionResultModel = types.model({
  summary: types.optional(types.string, ''),
  transcript: types.optional(types.array(MLTranscriptPhraseModel), []),
});

const BoundCallModel = types.model({
  id: types.optional(types.string, ''),
  record_url: types.optional(types.maybeNull(types.string), null),
  ai_record_url: types.optional(types.maybeNull(types.string), null),
  calldate: types.optional(types.string, ''), // TODO проверить
  direction: types.optional(types.string, ''),
  ml_transcription_result: types.optional(
    types.maybeNull(MLTranscriptionResultModel),
    null
  ),
  ai_transcription: types.optional(
    types.maybeNull(MLTranscriptionResultModel),
    null
  ),
  worker_name: types.optional(types.maybeNull(types.string), null),
});

export interface IBoundCall extends Instance<typeof BoundCallModel> {}

const RequestStatusesModel = types.model({
  boundCalls: types.optional(RequestStateModel, {}),
  saveRequestFiles: types.optional(RequestStateModel, {}),
  deleteRequestFiles: types.optional(RequestStateModel, {}),
  executorsWorkloadWeek: types.optional(RequestStateModel, {}),
  executorsWorkloadDay: types.optional(RequestStateModel, {}),
  createAppealFromRequest: types.optional(RequestStateModel, {}),
  syncRequest: types.optional(RequestStateModel, {}),
  categories: types.optional(RequestStateModel, {}),
  houseProviders: types.optional(RequestStateModel, {}),
  history: types.optional(RequestStateModel, {}),
  duplRequestGroups: types.optional(RequestStateModel, {}),
  xlsxReport: types.optional(RequestStateModel, {}),
  blanksReport: types.optional(RequestStateModel, {}),
  loadTemplates: types.optional(RequestStateModel, {}),
  createTemplate: types.optional(RequestStateModel, {}),
  updateTemplate: types.optional(RequestStateModel, {}),
  deleteTemplate: types.optional(RequestStateModel, {}),
  loadCurrentRequest: types.optional(RequestStateModel, {}),
  loadCount: types.optional(RequestStateModel, {}), //статус загрузки кол-ва заявок
  loadList: types.optional(RequestStateModel, {}), //статус загрузки списка заявок
  loadFilterCount: types.optional(RequestStateModel, {}), //статус загрузки кол-ва заявок по фильтрам
  deleteRequest: types.optional(RequestStateModel, {}), //состояние удаления заявки
  loadTroubleHistoryList: types.optional(RequestStateModel, {}),
  loadCurrentMaterials: types.optional(RequestStateModel, {}),
  loadCurrentServices: types.optional(RequestStateModel, {}),
  loadHistoryList: types.optional(RequestStateModel, {}),
  updateRequest: types.optional(RequestStateModel, {}), //статус обновления заявки
  copyRequest: types.optional(RequestStateModel, {}), //состояние копирования заявки
  tenantRequestStats: types.optional(RequestStateModel, {}),
  reserveFiles: types.optional(RequestStateModel, {}),
  getMessagesCounters: types.optional(RequestStateModel, {}), //непрочитанные сообщения по заявке (чат, мессенджер)
  getFullMessengerCounter: types.optional(RequestStateModel, {}), //общий каунтер непрочитанных сообщений по всем заявкам(диалогам)
  getRequestRating: types.optional(RequestStateModel, {}),
  createPublicLink: types.optional(RequestStateModel, {}),
  postPublicLink: types.optional(RequestStateModel, {}),
  aiAutogeneration: types.optional(RequestStateModel, {}), // автогенерация текста выполненных работ по заявке
});

const PreparedForDeleteIdsModel = types.model({
  requester_attachment: types.optional(types.array(types.string), []),
  tg_attach_start: types.optional(types.array(types.string), []),
  attachment: types.optional(types.array(types.string), []),
  tg_attach_final: types.optional(types.array(types.string), []),
  act: types.optional(types.array(types.string), []), // файл акта выполненных работ
  tg_act: types.optional(types.array(types.string), []),
});

const PreparedForRenameObjectModel = types.model({
  id: types.optional(types.string, ''),
  newFileName: types.optional(types.string, ''),
});

const PreparedForRenameObjectsModel = types.model({
  requester_attachment: types.optional(
    types.array(PreparedForRenameObjectModel),
    []
  ),
  tg_attach_start: types.optional(
    types.array(PreparedForRenameObjectModel),
    []
  ),
  attachment: types.optional(types.array(PreparedForRenameObjectModel), []),
  tg_attach_final: types.optional(
    types.array(PreparedForRenameObjectModel),
    []
  ),
  act: types.optional(
    types.array(PreparedForRenameObjectModel), // файл акта выполненных работ
    []
  ),
  tg_act: types.optional(types.array(PreparedForRenameObjectModel), []),
});

const LocalFileModel = types.model({
  file: types.frozen<File>(),
  options: types.maybe(types.frozen<{ [propName: string]: any }>()),
});

export interface ILocalFileModel extends Instance<typeof LocalFileModel> {}

const KindsModel: any = types.model({
  _id: types.optional(types.string, ''),
  default_child: types.maybeNull(types.string),
  name: types.optional(types.string, ''),
  slug: types.optional(types.string, ''),
  parent: types.optional(types.string, ''),
  inheritors: types.optional(types.map(types.late(() => KindsModel)), {}),
  actions: types.optional(types.map(types.late(() => KindsModel)), {}),
});

const ActionModel = types.model({
  name: types.string,
});

const WorkAreaModel = types.model({
  name: types.string,
  actions: types.optional(types.map(ActionModel), {}),
});

const SubcategoryModel = types.model({
  name: types.string,
  parent: types.string,
  work_areas: types.optional(types.map(WorkAreaModel), {}),
});

export const CategoryModel = types.model({
  name: types.string,
  subcategories: types.optional(types.map(SubcategoryModel), {}),
});

const RelatedRequestsBaseModel = types.model({
  _id: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  status: types.optional(RequestStatusMST, 'accepted'),
  description: types.optional(types.string, ''),
  created_at: types.optional(types.string, ''),
  start_at: types.optional(types.maybeNull(types.string), null),
  end_at: types.optional(types.maybeNull(types.string), null),
  delayed_until: types.optional(types.maybeNull(types.string), null),
  desired_start_at: types.optional(types.maybeNull(types.string), null),
  desired_end_at: types.optional(types.maybeNull(types.string), null),
  refused_at: types.optional(types.maybeNull(types.string), null),
  relationType: types.optional(
    types.enumeration(['sync', 'dupl'] as const),
    'sync'
  ),
  dupl_group_id: types.optional(types.maybeNull(types.string), null),
});
export interface IRelatedRequestsBase extends Instance<
  typeof RelatedRequestsBaseModel
> {}

const DuplGroupModel = types
  .model({
    _id: types.optional(types.string, ''),
    requests: types.optional(types.array(RelatedRequestsBaseModel), []),
  })
  .actions((self) => ({
    updateSelf(newItem: typeof self) {
      applySnapshot(self, newItem);
    },
  }));
export interface IDuplGroup extends Instance<typeof DuplGroupModel> {}

const RequestsFilterCountModel = types.model({
  accepted: types.optional(types.number, 0),
  emergency: types.optional(types.number, 0),
  overdue: types.optional(types.number, 0),
  run: types.optional(types.number, 0),
});

const RelatedModel = types
  .model({
    loading: types.optional(RequestStateModel, {}),
    updateStatus: types.optional(RequestStateModel, {}),
    deleteStatus: types.optional(RequestStateModel, {}),
    checked_request: types.optional(RelatedRequestsBaseModel, {}),
    selected_relationship: types.optional(types.string, 'sync'),
    dupl_request_group_id: types.optional(types.string, ''),
    dupl_request_groups: types.optional(DuplGroupModel, {}),
    dupl_request_groups_to_remove: types.optional(
      types.array(types.string),
      []
    ),
    searchResult: types.optional(types.array(types.frozen<IRequest>()), []),
    viewMode: types.optional(
      types.enumeration(['list', 'board'] as const),
      'list'
    ),
    fetch_attempted: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    updateTimeout: null as NodeJS.Timeout | null,
    getListStatus: RequestStateModel.create(),
  }))
  .views((self) => ({
    get currentRequest() {
      return getRoot<{ requests: { current: IRequest } }>(self).requests
        .current;
    },
    get allRequests(): IRelatedRequestsBase[] {
      return [
        ...self.dupl_request_groups.requests
          .filter((e) => e._id !== this.currentRequest._id)
          .map((request) => ({
            ...request,
            relationType: 'dupl' as IRelatedRequestsBase['relationType'],
            dupl_group_id: this.currentRequest.relations.dupl_request_group_id,
          })),
        ...this.currentRequest.relations.requests.map((r) => ({
          ...r,
          relationType: 'sync' as IRelatedRequestsBase['relationType'],
          dupl_group_id: null,
        })),
      ];
    },
    get preparedSearchResult() {
      const currentRelatedRequestsIDs = new Set(
        this.allRequests.map((e) => e._id)
      );

      return self.searchResult.filter(
        (el) =>
          !currentRelatedRequestsIDs.has(el._id) &&
          el._id !== this.currentRequest._id
      );
    },
    get relatedRequests() {
      return {
        dupl_request_group_id:
          self.selected_relationship === 'sync' &&
          self.checked_request.number !== ''
            ? self.dupl_request_group_id
            : null,
        regular_related_requests:
          self.selected_relationship === 'regular'
            ? [self.checked_request]
            : [],
      };
    },
    get relatedRequestsByRelationship() {
      if (self.selected_relationship === 'sync') {
        return self.dupl_request_groups.requests.filter(
          (request) => request._id !== self.checked_request._id
        );
      }

      return [];
    },
  }))
  .actions((self) => ({
    changeViewMode(mode: typeof self.viewMode) {
      self.viewMode = mode;
    },
    setSearchResult(requests: IRequest[]) {
      self.searchResult.replace(requests);
    },
    clearSearchResults() {
      self.searchResult.clear();
      self.getListStatus.clearStates();
    },
    setDuplGroupId(group_id: string) {
      self.dupl_request_group_id = group_id;
    },
    checkRequest(request: IRequest) {
      self.checked_request = {
        _id: request._id || '',
        number: request.number || '',
        status: request.status,
        description: request.description,
        created_at: request.created_at ?? '',
        start_at: request.execution.start_at,
        end_at: request.execution.end_at,
        delayed_until: request.execution.delayed_until,
        desired_start_at: request.execution.desired_start_at,
        desired_end_at: request.execution.desired_end_at,
        refused_at: '',
        relationType: 'dupl',
        dupl_group_id: request.relations.dupl_request_group_id,
      };
      self.dupl_request_group_id =
        request.relations.dupl_request_group_id || '';
    },
    clearCheckRequest() {
      self.checked_request = {
        _id: '',
        number: '',
        status: 'accepted',
        description: '',
        created_at: '',
        start_at: null,
        end_at: null,
        delayed_until: null,
        desired_start_at: null,
        desired_end_at: null,
        refused_at: null,
        relationType: 'sync',
        dupl_group_id: null,
      };
      self.selected_relationship = 'sync';
      self.dupl_request_group_id = '';
      self.dupl_request_groups_to_remove.clear();
    },
    setRelationship(relationship: string) {
      self.selected_relationship = relationship;
    },
    setSyncRequestsToRemove(request_id: string) {
      const index = self.dupl_request_groups_to_remove.indexOf(request_id);

      if (index > -1) {
        self.dupl_request_groups_to_remove.splice(index, 1);
      } else {
        self.dupl_request_groups_to_remove.push(request_id);
      }
    },
    clearRelatedRequests() {
      self.dupl_request_group_id = '';
      self.dupl_request_groups = cast({});
      self.dupl_request_groups_to_remove.clear();
      self.searchResult.clear();
      self.viewMode = 'list';
      self.fetch_attempted = false;
    },
    getList: flow(function* (params: Record<string, unknown>) {
      try {
        self.getListStatus.loadingStates();
        const response = yield Requests.GetRequests(killNulls(params));
        self.searchResult.replace(response);
        self.getListStatus.successStates();

        return self.searchResult;
      } catch (error) {
        console.error(error);
        self.getListStatus.errorStates();
      } finally {
        self.getListStatus.finallyStates();
      }
    }),
    getDuplRequests: flow(function* (duplGroupId?: string) {
      try {
        self.loading.loadingStates();

        const response: IDuplGroup[] =
          yield Requests.GetDuplRequestGroups(duplGroupId);

        if (duplGroupId) {
          self.dupl_request_groups = response[0];
        } else {
          const currentGroup = response.find(
            (e) => e._id === self.dupl_request_group_id
          );

          if (currentGroup) {
            self.dupl_request_groups = currentGroup;
          }
        }

        self.loading.successStates();

        return response;
      } catch (e) {
        self.loading.errorStates();
        console.error(e);
      } finally {
        self.loading.finallyStates();
        self.fetch_attempted = true;
      }
    }),
    postDuplRequests: flow(function* (
      // house_id: string,
      newRequestID: string
    ) {
      try {
        self.updateStatus.loadingStates();

        const data: {
          // house_id: string;
          requests: { _id: IRelatedRequest['_id'] }[];
        } = {
          // house_id: house_id,
          requests: [
            { _id: self.checked_request._id },
            {
              _id: newRequestID,
              // number: newRequest.number,
              // common_status: newRequest.common_status,
            },
          ],
        };
        const response: IDuplGroup = yield Requests.PostDuplRequestGroups(data);

        self.dupl_request_group_id = response._id;
        self.updateStatus.successStates();

        return response;
      } catch (e) {
        self.updateStatus.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.updateStatus.finallyStates();
      }
    }),
    updateDuplRequests: flow(function* (
      newRequestIds: string | string[],
      isNewRequest?: boolean
    ) {
      try {
        self.updateStatus.loadingStates();

        const params = {
          ...(isNewRequest ? { append: true } : undefined),
          requests:
            typeof newRequestIds === 'string'
              ? [
                  {
                    _id: newRequestIds,
                  },
                  ...self.dupl_request_groups.requests
                    .filter(
                      (el) =>
                        !self.dupl_request_groups_to_remove.includes(el._id) &&
                        el._id !== newRequestIds
                    )
                    .map((el) => ({
                      _id: el._id,
                    })),
                ]
              : newRequestIds.map((el) => ({
                  _id: el,
                })),
        };

        const response: IDuplGroup = yield Requests.UpdateDuplRequestGroups(
          self.dupl_request_group_id,
          params
        );

        self.dupl_request_group_id = response._id;
        self.dupl_request_groups = response;
        self.dupl_request_groups_to_remove.clear();
        self.updateStatus.successStates();

        return response;
      } catch (e) {
        self.updateStatus.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.updateStatus.finallyStates();
      }
    }),
    deleteDuplGroup: flow(function* (groupID: string) {
      try {
        self.deleteStatus.loadingStates();

        const response = yield Requests.DeleteDuplRequestGroups(groupID);
        self.dupl_request_groups = cast({});
        self.deleteStatus.successStates();

        return response;
      } catch (error) {
        self.deleteStatus.errorStates();

        throw error;
      } finally {
        self.deleteStatus.finallyStates();
      }
    }),
  }));

const CountInfoModel = types
  .model({
    conversation_id: types.optional(types.number, 0),
    unread_messages_amount: types.optional(types.number, 0),
  })
  .actions((self) => ({
    increaseCount() {
      self.unread_messages_amount = self.unread_messages_amount + 1;
    },
    decreaseCount() {
      if (self.unread_messages_amount === 0) {
        return;
      }

      self.unread_messages_amount = self.unread_messages_amount - 1;
    },
  }));

export interface ICountInfoModel extends Instance<typeof CountInfoModel> {} //TODO: НАДО ОБЪЕДИНИТЬ С ТИПАМИ МЕССЕНДЖЕРА!!!!!

const MessengerData = types
  .model({
    totalCount: types.optional(types.number, 0),
    counters: types.optional(types.array(CountInfoModel), []),
  })
  .views((self) => ({
    getCountById(id: number): number {
      const el = self.counters.find((item) => item.conversation_id === id);

      return el?.unread_messages_amount || 0;
    },
    getCountDataById(id: number): ICountInfoModel | null {
      return self.counters.find((item) => item.conversation_id === id) || null;
    },
  }))
  .actions((self) => ({
    setCounters(data: ICountInfoModel[]) {
      self.counters = cast(data);
    },
    setTotalCount(data: number) {
      self.totalCount = data;
    },
    increaseTotalCount() {
      self.totalCount = self.totalCount + 1;
    },
    decreaseTotalCount() {
      if (self.totalCount === 0) {
        return;
      }

      self.totalCount = self.totalCount - 1;
    },
  }));

/*
  Верхняя граница даты подачи для "Просроченных" (старше 2 часов).
  Если пользователь сам выбрал более раннюю границу, она сохраняется,
  чтобы таб не расширял выборку за пределы выбранного в фильтрах периода
*/
const getOverdueCreatedAtLte = (userCreatedAtLte?: string) => {
  const overdueBorder = moment().subtract(2, 'hours');

  if (
    userCreatedAtLte &&
    moment.utc(userCreatedAtLte).isBefore(overdueBorder)
  ) {
    return userCreatedAtLte;
  }

  return formatDateToISO(overdueBorder);
};

/*Подготовка параметров для запросов списка заявок и их количества*/
const prepareRequestsListParams = (rawParams: any) => {
  let params = prepareDatesInData(rawParams);
  params = normalizeActionDates(params);

  if (params.overduo) {
    params.overduo = null;
    params.status__in = ['run', 'accepted', 'delayed'];
    params.category = 'emergency';
    params.created_at__lte = getOverdueCreatedAtLte(params.created_at__lte);
  }

  /*
    source__in может отсутствовать: переходы "Смотреть все" из блоков
    истории заявок кладут в params только свой набор фильтров, а
    changeParams заменяет params целиком, не мержа с дефолтами.
  */
  if (
    params.source__in?.length &&
    (!params.hasOwnProperty('relations__appeal__source__in') ||
      !params.relations__appeal__source__in.includes('our_spb'))
  ) {
    delete params.relations__appeal__problem_number__in;
  }

  const newParams = killNulls(params);

  if (newParams.actions) {
    newParams.actions = JSON.stringify(newParams.actions); //TODO: в devtools отображается некрасиво, как в строке запроса, НО ошибки не выдаёт, вроде работает
  }

  return newParams;
};

export const RequestsStore = types
  .model({
    showCurtain: types.optional(types.boolean, false),
    list: types.optional(types.array(types.frozen<IRequest | ISignal>()), []), //список заявок //TODO: с типами что-то не так
    shouldUpdateList: types.optional(types.boolean, false),
    onCopyNumber: types.optional(types.boolean, false),
    executors: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ), //график сотрудников
    templates: types.optional(
      types.array(types.frozen<IRequestTemplate>()),
      []
    ), //шаблоны заявок
    currentTemplate: types.optional(types.frozen<IRequestTemplate>(), {}), //заявка из шаблона
    requestsCount: types.optional(types.maybeNull(types.number), null), //количество заявок (у псевдо-пагинатора)
    requestsFilterCount: types.optional(RequestsFilterCountModel, {}), //количество заявок сверху в фильтрах
    historyList: types.optional(
      types.array(types.frozen<IRequest | ISignal>()),
      []
    ), //последние заявки по квартире или сигналы по датчику
    troubleHistoryList: types.optional(
      types.array(types.frozen<IRequest>()),
      []
    ), //последние аварийные заявки по дому
    storageDocs: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ), //СТОРЕЙДЖ ДОКС //TODO: что это? (-> материалы из каталога)
    immutableStorageDocs: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ), //СТОРЕЙДЖ ДОКС НЕИЗМЕНЯЕМЫЙ
    completionActs: types.optional(
      types.array(types.frozen<ICompletionAct>()),
      []
    ), //КОМПЛЕЙШН ДОКС //TODO: что это? (-> усулги из каталога)
    immutableCompletionActs: types.optional(
      types.array(types.frozen<ICompletionAct>()),
      []
    ), //КОМПЛЕЙШН ДОКС НЕИЗМЕНЯЕМЫЙ
    currentMaterials: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
    temporaryMaterials: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ), //ВРЕМЕННО ПРИХРАНЯЕМЫЕ МАТЕРИАЛЫ ДЛЯ ИХ РАСШИФРОВКИ
    immutableCurrentMaterials: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
    currentServices: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
    preparedToSendFormDatas: types.optional(
      types.array(types.frozen<any>()),
      []
    ),
    onUpdateRequest: types.optional(types.boolean, false), //статус обновления заявки
    current: types.optional(types.frozen<{ [propName: string]: any }>(), {}), //текущая заявка или сигнал
    currentIsEdit: types.optional(types.boolean, false), //состояние текущей заявки (редактируемая или нет)
    currentOnEdit: types.optional(
      types.frozen<{ [propName: string]: any }>(),
      {}
    ), //редактируемая заявка или сигнал
    currentOnEditAttachments: types.optional(types.array(LocalFileModel), []), //вложения текущей заявки на редактирование
    currentCalls: types.optional(types.array(BoundCallModel), []), //звонки, привязанные к заявке или сигналу
    requestHistory: types.optional(
      types.array(types.frozen<RequestHistoryType | SignalHistoryType>()),
      []
    ), //история изменений заявки
    tenantRequestStats: types.optional(
      types.frozen<{ [propName: string]: any }>(),
      {}
    ), //статистика по заявкам
    params: types.optional(types.frozen<{ [propName: string]: any }>(), {
      limit: 20,
      offset: 0,
      // search_by: 'nothing',
      item_types: ['request', 'signal'],
      status__in: ['delayed', 'accepted'],
      signal__status__in: ['new'],
      source__in: [],
      category: '',
      subcategory: '',
      work_area: '',
    }), //параметры для запроса списка заявок
    kinds: types.optional(KindsModel, {}), //категории заявок в старом API
    all_categories: types.optional(types.map(CategoryModel), {}), //категории заявок новый API
    excelId: types.optional(types.string, ''), //id excel файла после "Выгрузить в Excel"
    newRequest_attachment: types.optional(types.array(LocalFileModel), []),
    newRequest_tg_attach_start: types.optional(types.array(LocalFileModel), []),
    newRequest_requester_attachment: types.optional(
      types.array(LocalFileModel),
      []
    ), //Новые файлы от сотрудника
    newRequest_tg_attach_final: types.optional(types.array(LocalFileModel), []),
    newRequest_act: types.optional(types.array(LocalFileModel), []),
    newRequest_tg_act: types.optional(types.array(LocalFileModel), []),
    to_delete: types.optional(PreparedForDeleteIdsModel, {}),
    to_rename: types.optional(PreparedForRenameObjectsModel, {}),
    houseProviders: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ), //управляющие организации
    contactPersonChanged: types.optional(types.boolean, false), //ИЗМЕНЯЛОСЬ ЛИ КОНТАКТНОЕ ЛИЦО
    controlMessage: types.optional(types.string, ''),
    statuses: types.optional(RequestStatusesModel, {}),
    searchMode: types.optional(types.string, '../list'),
    textSearchValue: types.optional(types.string, ''),
    executorsTable: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ), //табель (граф) сотрудников
    related: types.optional(RelatedModel, {}), // Cвязанные заявки
    lockUploadRequestFiles: types.optional(types.boolean, false),
    uniqueTenantsWithHouseFromRequests: types.optional(
      types.frozen<Record<string, TenantHouseProviderPair>>(),
      {}
    ),
    currentSelectedCoordinators: types.optional(CurrentSelectedCoordinators, {
      departments: [],
      positions: [],
      workers: [],
    }),
    messengerData: types.optional(MessengerData, {
      totalCount: 0,
      counters: [],
    }),
    fileSharedIds: types.optional(types.map(types.string), {}),
    aiAutogenerationRequestId: types.optional(types.string, ''), // id заявки, чтобы не запускать повторно
  })
  .views((self) => ({
    get currentRequest(): IRequest {
      return (
        self.currentIsEdit ? self.currentOnEdit : self.current
      ) as IRequest;
    },
    get conversationsIds(): string[] {
      return collectConversationIds(self.list as RequestListItem[]);
    },
    get filtersCount(): number {
      let count = 0;

      for (const key in self.params) {
        if (!['limit', 'offset'].includes(key) && self.params[key]) {
          if (Array.isArray(self.params[key])) {
            if (self.params[key].length) {
              count += 1;
            }
          } else if (typeof self.params[key] === 'object') {
            if (Object.keys(self.params[key]).length) {
              count += 1;
            }
          } else if (typeof self.params[key] === 'string') {
            if (self.params[key].length) {
              count += 1;
            }
          } else {
            count += 1;
          }
        }
      }

      return count;
    },
    filterTemplates(type: string) {
      return self.templates.filter((template) => {
        if (template._type !== type) {
          return false;
        }

        if (type === 'request') {
          return (
            template.category === self.currentOnEdit.category &&
            template.subcategory === self.currentOnEdit.subcategory
          );
        }

        return true;
      });
    },
    get isRequestSaveDisable() {
      return (
        self.statuses.saveRequestFiles.pending ||
        self.statuses.updateRequest.pending ||
        self.onUpdateRequest
      );
    },
    get allSubCategories() {
      const result = new Map<string, IRequestSubcategory>();

      self.all_categories.forEach((category) => {
        category.subcategories?.forEach((subCategory, subCategoryName) => {
          result.set(String(subCategoryName), subCategory);
        });
      });

      return result;
    },
    getSubCategoriesByCategory(name: string) {
      return self.all_categories.get(name)?.subcategories;
    },
    getSubCategoriesIdsByCategory(id: string) {
      const result: string[] = [];
      const categoryInheritors = self.kinds.inheritors.get(id);

      if (categoryInheritors?.inheritors) {
        categoryInheritors.inheritors.forEach((kind: IRequestKinds) => {
          result.push(kind._id);
        });
      }

      return result;
    },
    getWorkAreasByCategory(categoryName: string, subCategoryName: string) {
      return self.all_categories
        .get(categoryName)
        ?.subcategories?.get(subCategoryName)?.work_areas;
    },
    getCategoryName(categoryName: string): string {
      return self.all_categories.get(categoryName)?.name || '';
    },
    getSubcategoryName(categoryName: string, subCategoryName: string): string {
      return (
        self.all_categories
          .get(categoryName)
          ?.subcategories?.get(subCategoryName)?.name || ''
      );
    },
    getWorkAreaName(
      categoryName: string,
      subCategoryName: string,
      workAreaName: string
    ): string {
      return (
        this.getWorkAreasByCategory(categoryName, subCategoryName)?.get(
          workAreaName
        )?.name || ''
      );
    },
    get isWorkAreaSelectShown() {
      return !!this.getWorkAreasByCategory(
        this.currentRequest.category,
        this.currentRequest.subcategory ?? ''
      )?.size;
    },
    get allWorkAreas(): Map<string, typeof WorkAreaModel.Type> {
      const result = new Map();

      Array.from(self.all_categories.entries()).forEach(([, category]) => {
        const subcategories = category.subcategories;

        if (!subcategories) {
          return;
        }

        Array.from(subcategories.entries()).forEach(([, subCategory]) => {
          const workAreas = subCategory.work_areas;

          if (!workAreas) {
            return;
          }

          Array.from(workAreas.entries()).forEach(([area, workArea]) => {
            result.set(area, workArea);
          });
        });
      });

      return result;
    },
    get rateStarsLineArray() {
      return Array(5).fill(1);
    },
    getMaterialsList() {
      const materialsList: { [key: string]: any }[] = []; // материалы
      const defaultMaterialsList: { [key: string]: any }[] = []; // материалы по умолчанию
      const servicesList: { [key: string]: any }[] = []; // услуги
      let materialsTotal = 0;
      let servicesTotal = 0;

      if (self.currentOnEdit.resources?.warehouses) {
        self.currentOnEdit.resources.warehouses.forEach(
          (warehouse: { [key: string]: any }) => {
            warehouse?.items?.forEach((item: { [key: string]: any }) => {
              const newItem = { ...item, warehouseId: warehouse._id };
              materialsList.push(newItem);
              materialsTotal += item.quantity * item.price;
            });
          }
        );
      }

      // Исходное количество - сохранённое в заявке: остаток склада уже
      // уменьшен на него, поэтому в форме правки оно снова доступно.
      // Берётся из current только для той же заявки: при создании новой
      // в current может остаться ранее открытая.
      const savedWarehouses =
        self.current._id && self.current._id === self.currentOnEdit._id
          ? self.current.resources?.warehouses
          : undefined;

      if (savedWarehouses) {
        savedWarehouses.forEach((warehouse: { [key: string]: any }) => {
          warehouse?.items?.forEach((item: { [key: string]: any }) => {
            const newItem = { ...item, warehouseId: warehouse._id };
            defaultMaterialsList.push(newItem);
          });
        });
      }

      if (self.currentOnEdit.resources?.catalog_items) {
        self.currentOnEdit.resources?.catalog_items?.forEach(
          (item: { [key: string]: any }) => {
            servicesList.push(item);
            servicesTotal += item.quantity * item.price;
          }
        );
      }

      return {
        materialsList,
        materialsTotal,
        servicesList,
        servicesTotal,
        defaultMaterialsList,
      };
    },
    get sortedByDateCurrentCalls() {
      return [...self.currentCalls].sort((a, b) => {
        return moment(a.calldate, 'YYYY-MM-DDTHH:mm:ss').diff(
          moment(b.calldate, 'YYYY-MM-DDTHH:mm:ss')
        );
      });
    },
    getKindName(selectedKinds: string[]): string {
      return self.kinds.inheritors?.get(selectedKinds[0])?.name || '';
    },
    //TODO: проверить, нужен ли вообще c  новым API (сейчас используется только в reports)
    getSubKindName(selectedKinds: string[]): string {
      return (
        self.kinds.inheritors
          ?.get(selectedKinds[0])
          ?.inheritors.get(selectedKinds[1])?.name || ''
      );
    },
    getActions(
      category: string,
      subCategory: string | null,
      workArea: string | null
    ) {
      const data = getSnapshot(self.all_categories);
      let result: ISelectValue[] = [];

      const actions =
        data[category]?.subcategories[subCategory as string]?.work_areas[
          workArea as string
        ]?.actions;

      if (actions) {
        result = Object.keys(actions).map((actionName) => ({
          value: actionName,
          label: actions[actionName].name,
        }));
      }

      return [{ label: 'Без действия', value: '' }, ...result];
    },
    get tgAttachStartFiles() {
      if (!self.current?.execution?.attachment?.files) {
        return [];
      }

      return self.current.execution.attachment.files.filter(
        (file: IAttachmentFile) => file.tags?.includes('tg_attach_start')
      );
    },
    get tgAttachFinalFiles() {
      if (!self.current?.execution?.attachment?.files) {
        return [];
      }

      return self.current.execution.attachment.files.filter(
        (file: IAttachmentFile) => file.tags?.includes('tg_attach_final')
      );
    },
    get tgActFiles() {
      if (!self.current?.execution?.act?.files) {
        return [];
      }

      return self.current.execution.act.files.filter((file: IAttachmentFile) =>
        file.tags?.includes('tg_act')
      );
    },
    get webAttachFiles() {
      if (!self.current?.execution?.attachment?.files) {
        return [];
      }

      return self.current.execution.attachment.files.filter(
        (file: IAttachmentFile) =>
          !file.tags?.some?.((tag) => tag.includes('tg_'))
      );
    },
    get webActFiles() {
      if (!self.current?.execution?.act?.files) {
        return [];
      }

      return self.current.execution.act.files.filter(
        (file: IAttachmentFile) =>
          !file.tags?.some?.((tag) => tag.includes('tg_'))
      );
    },
    get hasRequesterAttachmentFiles() {
      return (
        !!self.newRequest_requester_attachment?.length ||
        !!self.current?.requester_attachment?.files?.length
      );
    },
    get hasAttachmentFiles() {
      return (
        !!self.newRequest_attachment.length ||
        self.current?.execution?.attachment?.files?.some(
          (file: IAttachmentFile) => !file.tags?.includes('tg_')
        )
      );
    },
    get hasActFiles() {
      return (
        !!self.newRequest_act.length ||
        self.current?.execution?.act?.files?.some(
          (file: IAttachmentFile) => !file.tags?.includes('tg_')
        )
      );
    },
    getRequestChangesHistoryList(
      full: boolean
    ): (RequestHistoryType | SignalHistoryType)[] {
      const reversed = [...self.requestHistory].reverse();

      if (!full) {
        return reversed.slice(0, 2);
      }

      return reversed;
    },
    get isFastSearchActive() {
      const isStatusCleared =
        Array.isArray(self.params.status__in) &&
        self.params.status__in.length === 0;

      return isStatusCleared;
    },
    get isAIDescriptionUntouched() {
      return (
        self.currentOnEdit.execution.is_ai_generated &&
        self.currentOnEdit.execution.description.trim() ===
          self.currentOnEdit.execution.ai_original_text
      );
    },
  }))
  .actions((self) => ({
    updateRequestInList(request: IRequest) {
      const currentRequestIndexInList = self.list.findIndex(
        (el) => el._id === request._id
      );

      if (currentRequestIndexInList > -1) {
        self.list[currentRequestIndexInList] = request;
      }
    },
    resolveSharedIds: flow(function* (attachments: IAttachmentFile[]) {
      try {
        const missing = attachments.filter(
          (a) => !self.fileSharedIds.has(a._id)
        );

        if (!missing.length) {
          return;
        }

        const sharedIds: Record<string, string> =
          yield Files.GetReqFileSharedIds({
            file_ids: missing.map((a) => a._id),
            filenames: missing.map((a) => a.name),
          });

        for (const data of Object.entries(sharedIds)) {
          self.fileSharedIds.set(...data);
        }

        return sharedIds;
      } catch (error) {
        return Promise.reject(error);
      }
    }),
    addFileSharedId(fileId: string, sharedId: string) {
      self.fileSharedIds.set(fileId, sharedId);
    },
    removeFileSharedId(fileId: string) {
      self.fileSharedIds.delete(fileId);
    },
    clearFileSharedIds() {
      self.fileSharedIds.clear();
    },
  }))
  .actions((self) => ({
    setCurrentIsEdit(value: boolean) {
      self.currentIsEdit = value;
    },
    changeCurrentOnEditField<T extends keyof IRequest>(
      field: T,
      value: IRequest[T]
    ) {
      self.currentOnEdit = { ...self.currentOnEdit, [field]: value };
    },
    /** Создает слепок текущих вложений для возможности отката изменений */
    createCurrentOnEditAttachmentsSnapshot(): ILocalFileModel[] {
      const snapshot = getSnapshot(self.currentOnEditAttachments);

      return cloneDeep(snapshot) as ILocalFileModel[];
    },
    replaceCurrentOnEditAttachmentsField(files: ILocalFileModel[]) {
      self.currentOnEditAttachments.replace(files);
    },
    addCurrentOnEditAttachmentsField(File: ILocalFileModel) {
      self.currentOnEditAttachments = cast([
        ...self.currentOnEditAttachments,
        File,
      ]);
    },
    removeCurrentOnEditAttachmentsField(id: string) {
      self.currentOnEditAttachments = cast(
        self.currentOnEditAttachments.filter(
          (file) => file.options?.file_id !== id
        )
      );
    },
    setTextSearchValue(value: string) {
      self.textSearchValue = value;
    },
    setSearchMode(value: string) {
      self.searchMode = value;
    },
    setShouldUpdateList(value: boolean) {
      self.shouldUpdateList = value;
    },
    setOnCopyNumber(value: boolean) {
      self.onCopyNumber = value;
    },
    setShowCurtain(value: boolean) {
      self.showCurtain = value;
    },
    setControlMessage(value: string) {
      self.controlMessage = value;
    },
    setTemporaryMaterials(value: { [key: string]: any }[]) {
      self.temporaryMaterials = cast(value);
    },
    setOnUpdateRequest(value: boolean) {
      self.onUpdateRequest = value;
    },
    setLockUploadRequestFiles(value: boolean) {
      self.lockUploadRequestFiles = value;
    },
    setUniqueTenantsWithHouseFromRequests(payload: {
      [key: string]: TenantHouseProviderPair;
    }) {
      self.uniqueTenantsWithHouseFromRequests = payload;
    },
    preSaveNewFiles(field: requestFileFieldType, files: any) {
      self[`newRequest_${field}`] = files;
    },
    reserveNewFile: flow(function* (filename: string) {
      try {
        self.statuses.reserveFiles.loadingStates();
        const res = yield Requests.GetReserveRequestFile({ filename });
        self.statuses.reserveFiles.successStates();

        return res;
      } catch (e) {
        console.error('requestsStore: promise reserveNewFile down');
        self.statuses.reserveFiles.errorStates();

        return Promise.reject(e);
      } finally {
        self.statuses.reserveFiles.finallyStates();
      }
    }),
    saveFile: flow(function* (id: string, file: File) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('importance', 'low');

        return yield Files.SaveFileToFilesMS(id, formData);
      } catch (e) {
        console.error('requestsStore: promise saveFile down');

        return Promise.reject(e);
      }
    }),
    countWarehouseItems(
      warehouseItem: any,
      warehouseId: string,
      count: number
    ) {
      if (warehouseItem) {
        const updatedResources = cloneDeep(self.currentOnEdit.resources);

        let isFound: boolean = false;

        updatedResources.warehouses.forEach(
          (warehouse: { [key: string]: any }) => {
            if (warehouse._id === warehouseId) {
              const items = [...warehouse.items];
              items.forEach((item: { [key: string]: any }, index: number) => {
                if (item._id === warehouseItem.id) {
                  isFound = true;

                  if (count > 0) {
                    item.quantity = count;
                  } else {
                    items.splice(index, 1);
                  }
                }
              });

              if (!isFound && count) {
                isFound = true;
                items.push({
                  _id: warehouseItem.id,
                  price: warehouseItem.price,
                  quantity: count,
                  name: warehouseItem.title,
                  stock_number: warehouseItem.stock_number,
                  unit_code: warehouseItem.unit_code,
                });
              }

              warehouse.items = [...items];
            }
          }
        );

        if (!isFound && count) {
          const newWarehouse = {
            _id: warehouseId,
            items: [
              {
                _id: warehouseItem.id,
                price: warehouseItem.price,
                quantity: count,
                name: warehouseItem.title,
                stock_number: warehouseItem.stock_number,
                unit_code: warehouseItem.unit_code,
              },
            ],
          };
          updatedResources.warehouses.push(newWarehouse);
        }

        self.currentOnEdit = {
          ...self.currentOnEdit,
          resources: updatedResources,
        };
      } else {
        //...
      }
    },
    countServiceItems(serviceItem: any, count: number) {
      if (serviceItem) {
        const updatedResources = cloneDeep(self.currentOnEdit.resources);
        let isFound: boolean = false;

        updatedResources.catalog_items.forEach(
          (item: { [key: string]: any }, index: number) => {
            if (item._id === serviceItem.id) {
              isFound = true;

              if (count > 0) {
                item.quantity = count;
                item.price = serviceItem.prices[0].value;
              } else {
                updatedResources.catalog_items.splice(index, 1);
              }
            }
          }
        );

        if (!isFound && count) {
          updatedResources.catalog_items.push({
            _id: serviceItem.id,
            name: serviceItem.title,
            price: serviceItem.prices[0].value,
            quantity: count,
          });
        }

        self.currentOnEdit = {
          ...self.currentOnEdit,
          resources: updatedResources,
        };
      } else {
        console.error('Invalid service item or count');
      }
    },
    editCompletionAct(
      act: ICompletionAct,
      service: { [key: string]: any },
      action: 'add' | 'delete',
      amount: number
    ) {
      //TODO: amount | index (add | delete)
      if (act) {
        /* При создании новой заявки, act.id всегда undefined.
         *  Если self.completionActs[0] использовать недопустимо, можно дописать
         *  действие в else у if (complAct) {}
         */

        //   let complAct = find(self.completionActs, { id: act.id });
        let complAct = self.completionActs[0];

        if (complAct) {
          complAct = clone(complAct);
          const acts = [...self.completionActs];
          const index = indexOf(acts, complAct);
          const subArr = [...complAct.positions];

          //TODO: НАДО МЕНЯТЬ САМ self.completionActs!!!
          if (action === 'add') {
            const a = find(complAct.positions, { service: service.id });
            const b = indexOf(complAct.positions, a);

            if (amount === 0) {
              subArr.splice(b, 1);
              complAct.positions = [...subArr];
              self.completionActs.splice(index, 1, complAct);

              return;
            }

            const newService = {
              amount,
              service: service.id,
              price: service.price,
              // price: service.prices[0].value,
            };

            if (a) {
              subArr.splice(b, 1, newService);
              complAct.positions = [...subArr];
            } else {
              complAct.positions = [...complAct.positions, newService];
            }

            self.completionActs.splice(0, 1, complAct);
          } else {
            subArr.splice(amount, 1);
            complAct.positions = [...subArr];
            self.completionActs.splice(index, 1, complAct);
            // acts.splice(index, 1, complAct);
            // self.completionActs = [...acts]; //TODO: ТИПЫ НЕ ТЕ, НО НАДО СДЕЛАТЬ
          }
        }
      } else {
        //TODO: когда нет актов НАДО КАК-ТО СОЗДАВАТЬ НОВЫЙ
        const newAct = {
          request: self.currentOnEdit.id,
          positions: [] as { [key: string]: any }[],
        };
        newAct.positions.push({
          service: service.id,
          price: service.price,
          amount,
        });
        const arr = [];
        arr.push(newAct);
        self.completionActs = [...arr] as any;
      }
    },
    setRequestEdit(request?: { [key: string]: any }) {
      self.currentOnEdit = clone(request ? request : self.current); //TODO: проверить что клон не связан!
      self.aiAutogenerationRequestId = '';
    },
    setRequestDetail(request: IRequest) {
      self.current = request;
      self.currentIsEdit = false;
    },
    changeParams(newParams: { [key: string]: any }) {
      self.params = newParams;
      self.requestsCount = null;
    },
    clearParamsToDefault() {
      self.params = {
        limit: 20,
        offset: 0,
        item_types: ['request', 'signal'],
        status__in: ['accepted', 'delayed'],
        signal__status__in: ['new'],
        source__in: [],
        tags: [],
        actions: {},
        relations__appeal__problem_number__in: '',
      };
      self.requestsCount = null;
    },
    changeCurrent(request: { [key: string]: any }) {
      self.currentOnEdit = request;
    },
    setCurrentOnEditField(field: string, value: any) {
      self.currentOnEdit = { ...self.currentOnEdit, [field]: value };
    },
    setCurrent(request: { [key: string]: any }) {
      self.current = request;
    },
    clearHistory() {
      self.troubleHistoryList = cast([]);
      self.statuses.loadTroubleHistoryList.clearStates();
      self.historyList = cast([]);
      self.statuses.loadHistoryList.clearStates();
    },
    clearCurrent() {
      self.current = {};
      self.requestHistory.replace([]);
    },
    clearCurrentSecondaryData() {
      self.currentOnEdit = {};
      self.controlMessage = '';
      self.tenantRequestStats = {};
      self.storageDocs = [] as any;
      self.immutableStorageDocs = [] as any;
      self.completionActs = [] as any;
      self.immutableCompletionActs = [] as any;
      self.currentMaterials = [] as any;
      self.immutableCurrentMaterials = [] as any;
      self.currentServices = [] as any;
      self.newRequest_attachment = [] as any; //TODO: сделать обнуление файлов
      self.newRequest_requester_attachment = [] as any;
      self.newRequest_act = [] as any;
      self.contactPersonChanged = false; //триггер изменения контактного лица
      self.statuses.loadHistoryList.fulfilled = false;
      self.statuses.loadTroubleHistoryList.fulfilled = false;
      self.temporaryMaterials = cast([]);
      self.executors = cast([]);
      self.to_delete = cast({});
      self.currentCalls = cast([]); // очистка звонков, привязанных к заявке
    },
    clearAddedByDispatcherDocsAndActs() {
      self.storageDocs = [] as any;
      self.completionActs = [] as any;
    },
    clearPreparedToSaveFormDatas() {
      self.preparedToSendFormDatas = cast([]);
    },
    setContactPersonChanged(bool: boolean) {
      self.contactPersonChanged = bool; //TODO: используется только в Edit заявки (возможно ещё в Create)
    },
    setCurrentTemplate(template?: IRequestTemplate) {
      if (template) {
        self.currentTemplate = template;
      } else {
        self.currentTemplate = {
          provider_id: '',
          body: '',
          name: '',
          _type: '',
          // id: '',
          // _binds: '',
        };
      }
    },
    clearTemporaryData() {
      /* функция очистки временных данных (например, id файла для загрузки) */
      self.excelId = '';
    },
    //Нет в новом API
    // setTenantStatsVote(good: boolean) {
    //   let newRate: { [key: string]: number } = {};
    //   let rateType: string = good ? 'up' : 'down';
    //   newRate[rateType] =
    //     (self.tenantStats.assessment
    //       ? self.tenantStats.assessment['rateType']
    //       : 0) + 1;

    //   self.tenantStats = {
    //     ...self.tenantStats,
    //     can_vote: false,
    //     assessment: {
    //       ...self.tenantStats.assessment,
    //       ...newRate,
    //     },
    //   };
    // },

    clearBoundCallsStatus() {
      self.statuses.boundCalls.clearStates();
    },
    updateCurrentRequestById(requestData: IRequest) {
      const index = self.list.findIndex(
        (request) => request._id === requestData._id
      );

      if (index !== -1) {
        const list = [...self.list];
        list.splice(index, 1, requestData);
        self.list = cast(list);
      }
    },
    clearLocalRequestFiles(field: requestFileFieldType) {
      applySnapshot(self[`newRequest_${field}`], []);
    },
    clearToDeleteFiles() {
      applySnapshot(self.to_delete, {});
    },
    clearToRenameFiles() {
      applySnapshot(self.to_rename, {});
    },
    clearHouseProviders() {
      self.houseProviders = cast([]);
    },
    getRequestsList: flow(function* (opts?: {
      queryParams?: typeof self.params;
      // silent нужен для запроса без состояний, и без записи в стор.
      // Просто сделать запрос и вернуть response
      silent?: boolean;
    }) {
      const isSilent = opts?.silent ?? false;

      try {
        const newParams = prepareRequestsListParams(
          opts?.queryParams ?? self.params
        );

        self.statuses.loadList.loadingStates();

        const response: RequestListItem[] = yield newParams.kristall_id
          ? Crystal.GetSignalsList(newParams)
          : Requests.GetRequests(newParams);

        if (!isSilent) {
          self.list.replace(response);
        }

        self.statuses.loadList.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.loadList.errorStates();

        return e;
      } finally {
        self.statuses.loadList.finallyStates();
      }
    }),
    getRequestRating: flow(function* (id: string | string[]) {
      try {
        self.statuses.getRequestRating.loadingStates();

        const response = yield Requests.GetRequestRating({ req_ids: id });
        self.statuses.getRequestRating.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.getRequestRating.errorStates();

        return Promise.reject(e);
      } finally {
        self.statuses.getRequestRating.finallyStates();
      }
    }),
    getRequestsFilterCount: flow(function* (opts?: {
      queryParams?: Record<string, any>;
    }) {
      try {
        self.statuses.loadFilterCount.loadingStates();
        // Фильтры те же, что у списка, но без пагинации. Из overduo берём
        // только границу по времени, как у списка (status__in и category
        // сводка не использует)
        const { limit, offset, overduo, ...rawParams } =
          opts?.queryParams ?? self.params;
        const params = prepareRequestsListParams(rawParams);

        if (overduo) {
          params.created_at__lte = getOverdueCreatedAtLte(
            params.created_at__lte
          );
        }

        const response = yield Requests.GetRequestsFiltersCount(params);
        self.requestsFilterCount = response;
        self.statuses.loadFilterCount.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.loadFilterCount.errorStates();
      } finally {
        self.statuses.loadFilterCount.finallyStates();
      }
    }),
    /*Сброс количества заявок по фильтрам*/
    clearRequestsFilterCount() {
      applySnapshot(self.requestsFilterCount, {});
    },
    deleteRequestFile(
      index: number,
      fileId: string,
      requestField: 'current' | 'currentOnEdit',
      field: requestFileFieldType,
      instantUpdate?: boolean
    ) {
      if (!instantUpdate) {
        if (field === 'requester_attachment') {
          self[requestField] = {
            ...self[requestField],
            [field]: {
              files: self[requestField]?.[field]?.files.filter(
                (_: any, i: number) => i !== index
              ),
            },
          };
        } else {
          self[requestField] = {
            ...self[requestField],
            execution: {
              ...self[requestField].execution,
              [field]: {
                files: self[requestField].execution?.[field]?.files.filter(
                  (_: any, i: number) => i !== index
                ),
              },
            },
          };
        }
      }

      const deletingFileIndex = self.to_rename[field]?.findIndex(
        (obj) => obj.id === fileId
      );

      if (deletingFileIndex >= 0) {
        self.to_rename[field].splice(deletingFileIndex, 1);
      }

      self.to_delete[field].push(fileId);
    },
    renameRequestFile(
      index: number,
      fileId: string,
      field: requestFileFieldType,
      newFileName: string
    ) {
      // Файл заявки будет переименован как в ней так и в GridFS
      const fieldCopy = [...self.current[field]];
      fieldCopy.splice(index, 1, {
        ...fieldCopy[index],
        file: {
          ...fieldCopy[index].file,
          name: newFileName,
        },
      });

      self.current = {
        ...self.current,
        [field]: fieldCopy,
      };
      const includedObjIndex = self.to_rename[field].findIndex(
        (obj) => obj.id === fileId
      );

      if (includedObjIndex >= 0) {
        self.to_rename[field].splice(includedObjIndex, 1, {
          id: fileId,
          newFileName,
        });
      } else {
        self.to_rename[field].push({
          id: fileId,
          newFileName,
        });
      }
    },
    spliceLocalFileByIndexAndField(
      field: requestFileFieldType,
      index: number,
      fileObj?: { file: File; otherInformation?: { [propName: string]: any } }
    ) {
      const localFilesCopy = [...self[`newRequest_${field}`]];
      fileObj
        ? localFilesCopy.splice(index, 1, {
            file: fileObj.file,
            options: undefined,
          })
        : localFilesCopy.splice(index, 1);

      self[`newRequest_${field}`] = localFilesCopy as any;
    },
    prepareFormDatas(
      preparedToDelete?: boolean,
      withoutCount?: boolean,
      specificField?: requestFileFieldType
    ) {
      const preparedToSendFiles: File[] = [];

      const setFilesToFormData = (field: requestFileFieldType) => {
        if (specificField && field !== specificField) {
          return;
        }

        const sourceFiles =
          field === 'requester_attachment'
            ? self.current
            : self.current.execution;

        if (self[`newRequest_${field}`].length) {
          let count = 0;

          if (!withoutCount) {
            count = preparedToDelete
              ? sourceFiles[`${field}`]?.files?.filter(
                  (el: { [propName: string]: any }) => el.file
                ).length - self.to_delete?.[`${field}`]?.length
              : sourceFiles?.[`${field}`]?.files?.filter(
                  (el: { [propName: string]: any }) => el.file
                ).length;
          }

          self[`newRequest_${field}`].forEach((fileObj) => {
            preparedToSendFiles.push(fileObj.file);
          });
        }
      };

      if (specificField) {
        setFilesToFormData(specificField);
      } else {
        setFilesToFormData('requester_attachment');
        setFilesToFormData('attachment');
        setFilesToFormData('act');
      }
      // setFilesToFormData('photos_tg_bot');
      // setFilesToFormData('attachments_tg_bot');
      // setFilesToFormData('completion_act_files_tg_bot');

      /*
          Array from для избежания ошибки:
          TS2802: Type 'Map<string, File>' can only be iterated through when using the '--downlevelIteration' flag
          or with a '--target' of 'es2015' or higher.
        */
      // const arrayFromMap = Array.from(preparedToSendFiles);
      // const splittedByMaxCapacityArrays = [];

      /*
        При попытке приложить слишком много элементов в form-data,
        может возникнуть 400 с "Неверные параметры запроса"
      */

      const formDatas: FormData[] = [];

      while (preparedToSendFiles.length) {
        const data = new FormData();
        const filesToSend = preparedToSendFiles.splice(0, 20);
        filesToSend.forEach((file) => {
          data.append('files', file);
        });
        formDatas.push(data);
      }

      self.preparedToSendFormDatas = cast(formDatas);
    },
    clearTemplates() {
      self.templates.clear();
    },
    getRequestsTemplates: flow(function* ({
      params,
    }: {
      params?: IParams;
    } = {}) {
      const preparedParams = params ? killNulls(params) : undefined;

      try {
        self.statuses.loadTemplates.loadingStates();
        const response = yield Requests.GetRequestTemplates(preparedParams);

        const merged = [...self.templates, ...response];
        const unique = merged.filter(
          (item, index, self) =>
            index === self.findIndex((i) => i._id === item._id)
        );
        self.templates.replace(unique);
        self.statuses.loadTemplates.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.loadTemplates.errorStates();
      } finally {
        self.statuses.loadTemplates.finallyStates();
      }
    }),
    getHistoryList: flow(function* (params) {
      try {
        self.statuses.loadHistoryList.loadingStates();

        self.historyList = yield !!params?.kristall_id
          ? Crystal.GetSignalsList(params)
          : Requests.GetRequests(params);

        self.statuses.loadHistoryList.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.loadHistoryList.errorStates();
      } finally {
        self.statuses.loadHistoryList.finallyStates();
      }
    }),
    getTroubleHistoryList: flow(function* (params) {
      //TODO: совершенно то же самое, что getHistoryList, просто записывает в другую переменную стора
      try {
        self.statuses.loadTroubleHistoryList.loadingStates();
        self.troubleHistoryList = yield Requests.GetRequests(params);
        self.statuses.loadTroubleHistoryList.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.loadTroubleHistoryList.errorStates();
      } finally {
        self.statuses.loadTroubleHistoryList.finallyStates();
      }
    }),
    getRequestChangesHistory: flow(function* (
      id: string,
      itemType: 'request' | 'signal'
    ) {
      try {
        self.statuses.history.loadingStates();
        const response = yield itemType === 'request'
          ? Requests.GetRequestHistory(id)
          : Crystal.GetSignalHistory(id);
        self.requestHistory = response;
        self.statuses.history.successStates();

        return response;
      } catch (e) {
        self.statuses.history.errorStates();
        console.error(e);
      } finally {
        self.statuses.history.finallyStates();
      }
    }),
    getRequestDetail: flow(function* (
      id,
      affectedAiReqNumber?: string,
      isSignal?: boolean
    ) {
      try {
        self.statuses.loadCurrentRequest.loadingStates();
        self.currentIsEdit = false;

        let response = yield isSignal
          ? Crystal.GetSignal(id)
          : Requests.GetRequest(id);
        response = {
          ...response,
          item_type: isSignal ? 'signal' : 'request',
        };

        const currentRequestIndexInList = self.list.findIndex((el) =>
          affectedAiReqNumber
            ? el.number === affectedAiReqNumber
            : el._id === response._id
        );

        if (currentRequestIndexInList > -1) {
          self.list[currentRequestIndexInList] = response;
        }

        self.current = response;
        self.currentOnEdit = response;
        self.aiAutogenerationRequestId = '';
        self.statuses.loadCurrentRequest.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.loadCurrentRequest.errorStates();

        return Promise.reject(e);
      } finally {
        self.statuses.loadCurrentRequest.finallyStates();
      }
    }),
    cutRequestFromFilteredListByStatus: flow(function* (id) {
      try {
        self.statuses.updateRequest.loadingStates();
        const data = yield Requests.GetRequest(id);
        const indx = self.list.findIndex((el) => el._id === id);

        if (indx >= 0 && !self.params.status__in.includes(data.status)) {
          self.list.splice(indx, 1);
        }
      } catch (e) {
        console.error(e);
        self.statuses.updateRequest.errorStates();
      } finally {
        self.statuses.updateRequest.finallyStates();
      }
    }),
    getCategoriesTree: flow(function* () {
      try {
        self.statuses.categories.loadingStates();

        const response = yield Requests.GetCategoriesTree();

        self.all_categories = response.categories;
        self.statuses.categories.successStates();
      } catch (e) {
        self.statuses.categories.errorStates();
        console.error(e);
      } finally {
        self.statuses.categories.finallyStates();
      }
    }),
    getTenantRequestStats: flow(function* (tenantId: string) {
      if (tenantId === '000000000000000000000000') {
        return;
      }

      try {
        self.statuses.tenantRequestStats.loadingStates();

        const response = yield Requests.GetTenantRequestStats(tenantId);
        self.tenantRequestStats = response;
        self.statuses.tenantRequestStats.successStates();

        return response;
      } catch (e) {
        self.statuses.tenantRequestStats.errorStates();
        console.error(e);
      } finally {
        self.statuses.tenantRequestStats.finallyStates();
      }
    }),
    updateRequest: flow(function* (data: any) {
      try {
        self.statuses.updateRequest.loadingStates();
        const response = yield Requests.UpdateRequest(
          self.current._id || self.currentOnEdit._id,
          data,
          self.current?.revision ?? self.currentOnEdit?.revision
        );

        self.statuses.updateRequest.successStates();
        self.current = response;
        self.updateRequestInList(response);

        return self.current;
      } catch (e) {
        console.error(e);
        self.statuses.updateRequest.errorStates();

        yield handleConcurrentUpdate(self, e);

        throw e;
      } finally {
        self.statuses.updateRequest.finallyStates();
      }
    }),
    updateRequestStatus: flow(function* (data: any) {
      try {
        self.statuses.updateRequest.loadingStates();

        /* координаторы могут быть только в статусе "принята" */
        if (data.status !== 'accepted' && !!data?.coordinators?.length) {
          data = {
            ...data,
            execution: { ...data.execution, coordinators: [] },
          };
        }

        const response = yield Requests.UpdateRequestStatus(
          self.current._id || self.currentOnEdit._id,
          data,
          self.current?.revision ?? self.currentOnEdit?.revision
        );
        self.statuses.updateRequest.successStates();
        self.current = response;

        return self.current;
      } catch (e) {
        console.error(e);
        self.statuses.updateRequest.errorStates();

        yield handleConcurrentUpdate(self, e);

        throw e;
      } finally {
        self.statuses.updateRequest.finallyStates();
      }
    }),
    autogenerateExecutionDescription: flow(function* (textDescription: string) {
      if (!textDescription) {
        return;
      }

      self.statuses.aiAutogeneration.loadingStates();

      try {
        const { answer } = yield Requests.AutoGenerateAiAnswer({
          text: textDescription,
        });

        self.currentOnEdit = {
          ...self.currentOnEdit,
          execution: {
            ...self.currentOnEdit.execution,
            description: answer,
            is_ai_generated: true,
            ai_original_text: answer,
          },
        };

        self.statuses.aiAutogeneration.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.aiAutogeneration.errorStates();

        throw e;
      } finally {
        self.statuses.aiAutogeneration.finallyStates();
      }
    }),
    setAiAutogenerationRequestId(id: string) {
      self.aiAutogenerationRequestId = id;
    },
    updateSignalStatus: flow(function* () {
      try {
        self.statuses.updateRequest.loadingStates();
        const payload = {
          new_status: self.currentOnEdit.status,
          reason: self.currentOnEdit.status_reason,
          repair_until: self.currentOnEdit.repair_until
            ? moment(self.currentOnEdit.repair_until).utc().toISOString()
            : undefined,
        };

        if (payload.new_status !== 'in_repair') {
          delete payload.repair_until;
        }

        if (!payload.reason) {
          throw new Error('Описание не может быть пустым');
        }

        if ('repair_until' in payload && !payload.repair_until) {
          throw new Error('Время в ремонте не может быть пустым');
        }

        const response = yield Crystal.UpdateSignalStatus(
          self.current._id,
          payload
        );
        self.statuses.updateRequest.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.updateRequest.errorStates();

        throw e;
      } finally {
        self.statuses.updateRequest.finallyStates();
      }
    }),
    copyRequest: flow(function* (requestId: string) {
      try {
        self.statuses.copyRequest.loadingStates();
        const response = yield Requests.CopyRequest(requestId);
        self.statuses.copyRequest.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.copyRequest.errorStates();

        return Promise.reject(error);
      } finally {
        self.statuses.copyRequest.finallyStates();
      }
    }),
    postFormData: flow(function* (
      formData: FormData,
      options: { requestId?: string; fileType: requestFileFieldType }
    ) {
      self.statuses.saveRequestFiles.loadingStates();

      try {
        const response = yield Requests.SendFiles(
          options?.requestId || self.current._id,
          formData,
          options?.fileType || 'requester_attachment'
        );
        self.statuses.saveRequestFiles.successStates();

        return response;
      } catch (e: any) {
        self.statuses.saveRequestFiles.errorStates();

        if (e.status === 500) {
          /*
            Иногда возникает редкая 500 ошибка NetworkTimeout,
            возможно только на localhost
          */
          throw new Error(e);
        }
      } finally {
        self.statuses.saveRequestFiles.finallyStates();
      }
    }),
    // Не используется в новом API но может пригодиться
    // deleteOrRenameRequestFiles: flow(function* () {
    //   self.statuses.deleteRequestFiles.fulfilled = false;
    //   self.statuses.deleteRequestFiles.error = false;
    //   self.statuses.deleteRequestFiles.pending = true;

    //   try {
    //     const preparedToSendFiles: string[][] = [];

    //     for (const [key, value] of Object.entries(self.to_delete)) {
    //       if (value.length) {
    //         value.forEach((id: string) => {
    //           const indx = self.current[key]?.files?.findIndex(
    //             (el: { [propName: string]: any }) => el._id === id
    //           );
    //           preparedToSendFiles.push([`pull__${key}__${indx}`, id]);
    //         });
    //       }
    //     }

    //     for (const [key, value] of Object.entries(self.to_rename)) {
    //       if (value.length) {
    //         value.forEach(
    //           ({ id, newFileName }: { id: string; newFileName: string }) => {
    //             const indx = self.current[key].findIndex(
    //               (el: { [propName: string]: any }) => el._id === id
    //             );

    //             if (indx >= 0) {
    //               preparedToSendFiles.push([
    //                 `rename__${key}__${indx}__${newFileName}`,
    //                 id,
    //               ]);
    //             }
    //           }
    //         );
    //       }
    //     }

    //     const splittedByMaxCapacityArrays = [];

    //     while (preparedToSendFiles.length > 0) {
    //       splittedByMaxCapacityArrays.push(preparedToSendFiles.splice(0, 20));
    //     }

    //     const formDatas: FormData[] = [];

    //     splittedByMaxCapacityArrays.forEach((arr) => {
    //       const data = new FormData();
    //       arr.forEach((entry) => data.append(entry[0], entry[1]));
    //       formDatas.push(data);
    //     });

    //     // на всякий случай, т.к. self.current очищается
    //     const requestId = self.current._id;

    //     yield Promise.allSettled(
    //       formDatas.map(async (fd) => {
    //         try {
    //           await Requests.SendFiles(requestId, fd);
    //         } catch (e: any) {
    //           if (e.status === 500) {
    //             /*
    //               Единоразовый повторный вызов.
    //               Иногда может возникнуть редкая 500 ошибка NetworkTimeout,
    //               возможно актуально только для localhost
    //             */
    //             await Requests.SendFiles(requestId, fd);
    //           }
    //           self.statuses.deleteRequestFiles.error = true;
    //           console.log(e);
    //         }
    //       })
    //     );

    //     self.statuses.deleteRequestFiles.fulfilled = true;
    //   } finally {
    //     self.statuses.deleteRequestFiles.pending = false;
    //   }
    // }),
    deleteRequest: flow(function* (id: string) {
      try {
        self.statuses.deleteRequest.loadingStates();
        const response = yield Requests.DeleteRequest(id);
        self.statuses.deleteRequest.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.deleteRequest.errorStates();
      } finally {
        self.statuses.deleteRequest.finallyStates();
      }
    }),
    createRequestTemplate: flow(function* (template: IRequestTemplate) {
      try {
        self.statuses.createTemplate.loadingStates();
        yield Requests.CreateRequestTemplate(template);
        self.statuses.createTemplate.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.createTemplate.errorStates();
      } finally {
        self.statuses.createTemplate.finallyStates();
      }
    }),
    updateRequestTemplate: flow(function* (template: IRequestTemplate) {
      try {
        self.statuses.updateTemplate.loadingStates();
        const updatedTemplate = yield Requests.UpdateRequestTemplate(template);
        const foundTemplateIndex = self.templates.findIndex(
          (t) => t._id === updatedTemplate._id
        );

        if (foundTemplateIndex !== -1) {
          self.templates[foundTemplateIndex] = updatedTemplate;
        }

        self.statuses.updateTemplate.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.updateTemplate.errorStates();
      } finally {
        self.statuses.updateTemplate.finallyStates();
      }
    }),
    deleteRequestTemplate: flow(function* (id: string) {
      try {
        self.statuses.deleteTemplate.loadingStates();
        yield Requests.DeleteRequestTemplate(id);

        self.templates.replace(self.templates.filter((t) => t._id !== id));
        self.statuses.deleteTemplate.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.deleteTemplate.errorStates();

        throw e;
      } finally {
        self.statuses.deleteTemplate.finallyStates();
      }
    }),
    initUnloadExcel: flow(function* () {
      try {
        const params = prepareDatesInData(self.params);

        if (!!params?.number?.length) {
          delete params.status__in;
        }

        const response = yield Requests.UnloadExcel(params);
        self.excelId = response.report_id;
      } catch (e) {
        console.error(e);
      }
    }),
    getExcelStatus: flow(function* () {
      try {
        return yield Requests.GetExcelId({
          report_id: self.excelId,
          data: 'status',
        });
      } catch (e) {
        console.error(e);
      }
    }),
    generateBlankById: flow(function* (id: string, print: boolean = true) {
      try {
        self.statuses.blanksReport.loadingStates();

        const response = yield Requests.GetBlankByRequestId(id);

        if (!response.ok) {
          throw new Error(
            `Ошибка при скачивании файла: ${response.statusText}`
          );
        }

        if (print) {
          const blob = yield response.blob();
          openBlobInNewTabAndPrint(blob);
        }

        self.statuses.blanksReport.successStates();

        return response;
      } catch (e) {
        self.statuses.blanksReport.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses.blanksReport.finallyStates();
      }
    }),
    generateBlanksList: flow(function* (date?: string) {
      try {
        self.statuses.blanksReport.loadingStates();

        const params = prepareDatesInData(self.params);
        const newParams = killNulls(params);

        if (newParams.actions) {
          newParams.actions = JSON.stringify(newParams.actions);
        }

        const response: any = yield Requests.GetRequestsBlanks({
          ...newParams,
          created_at__gte: date,
        });

        if (!response.ok) {
          throw new Error(
            `Ошибка при скачивании файла: ${response.statusText}`
          );
        }

        const blob = yield response.blob();
        openBlobInNewTabAndPrint(blob);

        self.statuses.blanksReport.successStates();
      } catch (e) {
        self.statuses.blanksReport.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.statuses.blanksReport.finallyStates();
      }
    }),
    downloadXLSXReport: flow(function* () {
      try {
        self.statuses.xlsxReport.loadingStates();
        const params = prepareDatesInData(self.params);
        const newParams = killNulls(params);

        // Необходимо ограничение на выгрузку с периодами максимум в полгода
        const checkPeriod = (field: 'created' | 'execution' | 'desired') => {
          const period: { gte?: string; lt?: string; name?: string } = {};

          switch (field) {
            case 'execution':
              period.gte = newParams.execution__end_at__gte;
              period.lt = newParams.execution__end_at__lte;
              period.name = 'Дата выполнения заявки';
              break;
            case 'desired':
              period.gte = newParams.execution__desired_start_at__gte;
              period.lt = newParams.execution__desired_end_at__lte;
              period.name = 'Желаемое время выполнения заявки';
              break;
            default:
              period.gte = newParams.created_at__gte;
              period.lt = newParams.created_at__lte;
              period.name = 'Дата подачи заявки';
              break;
          }

          if (period.gte) {
            const startDate = moment(period.gte);
            const maxEndDate = cloneDeep(startDate).add(6, 'months');
            const endDate = moment(period.lt);

            if (endDate.isAfter(maxEndDate)) {
              return Promise.reject({
                message: 'invalid period',
                field: period.name,
              });
            }
          }

          return Promise.resolve('is valid period');
        };

        // Поочередная проверка валидности возможных периодов
        yield checkPeriod('created');
        yield checkPeriod('execution');
        yield checkPeriod('desired');

        if (newParams.actions) {
          newParams.actions = JSON.stringify(newParams.actions);
        }

        const response = yield Requests.DownloadRequestsXlsx(newParams);

        if (!response.ok) {
          throw new Error(
            `Ошибка при скачивании файла: ${response.statusText}`
          );
        }

        // На случай, если в заголовках не будет Content-Disposition
        let filename = 'Отчёт (Журнал заявок).xlsx';
        const disposition = response.headers.get('Content-Disposition');

        if (disposition) {
          const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);

          if (utf8Match) {
            filename = decodeURIComponent(utf8Match[1]);
          } else {
            const match = disposition.match(/filename="?([^";]+)"?/i);

            if (match) {
              filename = match[1];
            }
          }
        }

        const blob = yield response.blob();
        downloadBlobFile(blob, filename);

        self.statuses.xlsxReport.successStates();

        return response;
      } catch (error) {
        self.statuses.xlsxReport.errorStates();
        console.error(error);

        return Promise.reject(error);
      } finally {
        self.statuses.xlsxReport.finallyStates();
      }
    }),
    createControlMessage: flow(function* (id?: string, text?: string) {
      try {
        return yield Requests.CreateControlMessage(
          id ?? self.currentOnEdit._id,
          {
            text: text ?? self.controlMessage,
          }
        );
      } catch (e) {
        console.error(e);

        throw e;
      }
    }),
    readControlMessage: flow(function* (id?: string) {
      try {
        return yield Requests.ReadControlMessage(id ?? self.currentOnEdit._id);
      } catch (e) {
        console.error(e);

        throw e;
      }
    }),
    getStorageDocs: flow(function* (id: string) {
      try {
        const response = yield Requests.GetStorageDocsByRequest(id);
        self.storageDocs = response;
        self.immutableStorageDocs = JSON.parse(JSON.stringify(response));

        return response;
      } catch (e) {
        console.error(e);
      }
    }),
    getCompletionActs: flow(function* (id: string) {
      try {
        const response = yield Requests.GetCompletionActsByRequest(id);
        self.completionActs = response;
        self.immutableCompletionActs = response;

        return response;
      } catch (e) {
        console.error(e);
      }
    }),
    updateCompletionActs: flow(function* (act: ICompletionAct) {
      try {
        return yield Requests.UpdateCompletionActs(act);
      } catch (e) {
        console.error(e);
      }
    }),
    createCompletionActs: flow(function* (act) {
      try {
        return yield Requests.CreateCompletionActs(act);
      } catch (e) {
        console.error(e);
      }
    }),
    getMaterials: flow(function* (params: IParams) {
      try {
        self.statuses.loadCurrentMaterials.loadingStates();
        const response = yield Catalogue.GetMaterials(params);
        self.currentMaterials = response.results;
        self.immutableCurrentMaterials = JSON.parse(
          JSON.stringify(response.results)
        );
        self.statuses.loadCurrentMaterials.successStates();

        return response.results;
      } catch (e) {
        console.error(e);
        self.statuses.loadCurrentMaterials.errorStates();
      } finally {
        self.statuses.loadCurrentMaterials.finallyStates();
      }
    }),
    getServices: flow(function* (params: IParams) {
      try {
        self.statuses.loadCurrentServices.loadingStates();
        const response = yield Catalogue.GetServices(params);
        self.currentServices = response;
        self.statuses.loadCurrentServices.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.loadCurrentServices.errorStates();
      } finally {
        self.statuses.loadCurrentServices.finallyStates();
      }
    }),
    getHouseProviders: flow(function* (houseId: string) {
      try {
        self.statuses.houseProviders.loadingStates();
        const response = yield Requests.GetHouseProviders(houseId);
        self.houseProviders = response.results;
        self.statuses.houseProviders.successStates();

        return response.results;
      } catch (e) {
        self.statuses.houseProviders.errorStates();
        console.error(e);
      } finally {
        self.statuses.houseProviders.finallyStates();
      }
    }),
    getEmployeesWeekSchedule: flow(function* (params) {
      try {
        self.statuses.executorsWorkloadWeek.loadingStates();
        const response = yield Requests.GetEmployeesWorkSchedule(params);
        self.executors = response;
        self.statuses.executorsWorkloadWeek.successStates();

        return response;
      } catch (e) {
        self.statuses.executorsWorkloadWeek.errorStates();
        console.error(e);
      } finally {
        self.statuses.executorsWorkloadWeek.finallyStates();
      }
    }),
    getEmployeeDailySchedules: flow(function* (params) {
      try {
        self.statuses.executorsWorkloadDay.loadingStates();
        const response = yield Requests.GetEmployeesWorkSchedule(params);
        self.executorsTable = response;
        self.statuses.executorsWorkloadDay.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.executorsWorkloadDay.errorStates();
      } finally {
        self.statuses.executorsWorkloadDay.finallyStates();
      }
    }),
    clearExecutors() {
      self.executors = cast([]);
      self.statuses.executorsWorkloadWeek.fulfilled = false;
    },
    clearExecutorsTable() {
      self.executorsTable = cast([]);
      self.statuses.executorsWorkloadDay.fulfilled = false;
    },
    getBoundCallsByIds: flow(function* (call_ids?: string[]) {
      self.statuses.boundCalls.loadingStates();

      try {
        self.currentCalls = yield Requests.GetBoundCallsByIds(
          call_ids ?? self.current.relations.call_ids
        );
        self.statuses.boundCalls.successStates();
      } catch (e) {
        self.statuses.boundCalls.errorStates();
        console.error(e);
      } finally {
        self.statuses.boundCalls.finallyStates();
      }
    }),
    createAppealFromRequest: flow(function* (
      requestId: string,
      ticketType: TicketType,
      departmentId: string
    ) {
      self.statuses.createAppealFromRequest.loadingStates();

      try {
        const data = {
          _type: ticketType,
          department_id: departmentId,
        };

        const response = yield Requests.CreateAppealFromRequest(
          requestId,
          data
        );

        self.current = response;

        self.statuses.createAppealFromRequest.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.createAppealFromRequest.errorStates();

        throw e;
      } finally {
        self.statuses.createAppealFromRequest.finallyStates();
      }
    }),
    getMessagesCounters: flow(function* () {
      try {
        if (!self.conversationsIds.length) {
          return;
        }

        self.statuses.getMessagesCounters.loadingStates();

        const response: ICountInfoModel[] =
          yield Messenger.GetConversationsCounters(self.conversationsIds);

        self.messengerData.setCounters(response);

        self.statuses.getMessagesCounters.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.getMessagesCounters.errorStates();
      } finally {
        self.statuses.getMessagesCounters.finallyStates();
      }
    }),
    getFullMessengerCounter: flow(function* () {
      try {
        self.statuses.getFullMessengerCounter.loadingStates();

        const response = yield Messenger.GetFullCounter();
        self.messengerData.setTotalCount(response.unread_message_count);

        self.statuses.getFullMessengerCounter.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.getFullMessengerCounter.errorStates();
      } finally {
        self.statuses.getFullMessengerCounter.finallyStates();
      }
    }),
    createPublicLink: flow(function* () {
      try {
        self.statuses.createPublicLink.loadingStates();

        const response = yield Requests.CreatePublicLink(
          self.currentRequest._id
        );

        self.statuses.createPublicLink.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.createPublicLink.errorStates();

        throw e;
      } finally {
        self.statuses.createPublicLink.finallyStates();
      }
    }),
    postPublicLink: flow(function* (channel: SendingChannel) {
      try {
        self.statuses.postPublicLink.loadingStates();

        const response = yield Requests.PostPublicLink(
          self.currentRequest._id,
          channel
        );

        self.statuses.postPublicLink.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.postPublicLink.errorStates();

        throw e;
      } finally {
        self.statuses.postPublicLink.finallyStates();
      }
    }),
  }));

export interface IRequestsStore extends Instance<typeof RequestsStore> {}
